/**
 * IndustryIllustrations.jsx
 *
 * 8 industry-specific illustrations built in the SAME polished, layered
 * flat-fintech style used elsewhere on the Swipetronics site
 * (see FintechIllustrations.jsx: GrowthIllustration, EcosystemIllustration,
 *  SecurityIllustration…):
 *
 *   • Light soft-blue panel backdrop (EAF3FF → F4F8FF) with dotted texture
 *   • Radial accent halo + rotated decorative squares/circles
 *   • Hero object + one or two floating cards with soft drop shadows
 *   • Palette strictly limited to navy #061A3A / #0A2552, blue #0072FF /
 *     #3A8EFF, soft blue #EAF3FF, white and light neutrals
 *
 * Each illustration renders inside a 720×220 landscape viewBox that fits the
 * card top area cleanly, matching the composition rhythm of the rest of
 * the site.
 */

/* ── Shared defs (unique per instance id) ──────────────────────────── */
function IDefs({ id }) {
  return (
    <defs>
      <pattern id={`${id}-dots`} x="0" y="0" width="18" height="18" patternUnits="userSpaceOnUse">
        <circle cx="1.4" cy="1.4" r="1.4" fill="#0072FF" fillOpacity="0.16" />
      </pattern>
      <linearGradient id={`${id}-panel`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#EAF3FF" />
        <stop offset="100%" stopColor="#F4F8FF" />
      </linearGradient>
      <linearGradient id={`${id}-card`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#FFFFFF" />
        <stop offset="100%" stopColor="#F6FAFF" />
      </linearGradient>
      <linearGradient id={`${id}-accent`} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#0072FF" />
        <stop offset="100%" stopColor="#3A8EFF" />
      </linearGradient>
      <linearGradient id={`${id}-navy`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#0A2552" />
        <stop offset="100%" stopColor="#061A3A" />
      </linearGradient>
      <radialGradient id={`${id}-halo`} cx="50%" cy="50%" r="55%">
        <stop offset="0%" stopColor="#0072FF" stopOpacity="0.22" />
        <stop offset="70%" stopColor="#0072FF" stopOpacity="0.05" />
        <stop offset="100%" stopColor="#0072FF" stopOpacity="0" />
      </radialGradient>
      <filter id={`${id}-shadow`} x="-30%" y="-30%" width="160%" height="160%">
        <feGaussianBlur in="SourceAlpha" stdDeviation="6" />
        <feOffset dx="0" dy="10" result="off" />
        <feComponentTransfer>
          <feFuncA type="linear" slope="0.22" />
        </feComponentTransfer>
        <feMerge>
          <feMergeNode />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
      <filter id={`${id}-soft`} x="-30%" y="-30%" width="160%" height="160%">
        <feGaussianBlur in="SourceAlpha" stdDeviation="3" />
        <feOffset dx="0" dy="4" result="off" />
        <feComponentTransfer>
          <feFuncA type="linear" slope="0.16" />
        </feComponentTransfer>
        <feMerge>
          <feMergeNode />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>
  );
}

/* ── Panel backdrop: soft blue panel + dots + halo + decorative accents ─ */
function IPanel({ id, accents }) {
  return (
    <g>
      <rect x="0" y="0" width="720" height="220" rx="22" fill={`url(#${id}-panel)`} />
      <rect x="0" y="0" width="720" height="220" rx="22" fill={`url(#${id}-dots)`} />
      <ellipse cx="360" cy="110" rx="300" ry="130" fill={`url(#${id}-halo)`} />
      {accents}
    </g>
  );
}

/* Standard accent decorations shared across all 8 */
const StandardAccents = () => (
  <g>
    <circle cx="60" cy="46" r="30" fill="#0072FF" fillOpacity="0.08" />
    <circle cx="672" cy="182" r="34" fill="#0072FF" fillOpacity="0.09" />
    <rect x="612" y="26" width="38" height="38" rx="10" fill="#0072FF" fillOpacity="0.07" transform="rotate(14 631 45)" />
    <rect x="40"  y="152" width="34" height="34" rx="10" fill="#061A3A" fillOpacity="0.05" transform="rotate(-10 57 169)" />
  </g>
);

/* Reusable framed base */
function Base({ id, children, label }) {
  return (
    <svg viewBox="0 0 720 220" xmlns="http://www.w3.org/2000/svg" className="w-full h-full block" role="img" aria-label={label}>
      <IDefs id={id} />
      <IPanel id={id} accents={<StandardAccents />} />
      {children}
    </svg>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   1) RESTAURANTS
   Hero: tableside ordering tablet with menu list.
   Orbit: check receipt card + tips pill.
   ══════════════════════════════════════════════════════════════════════ */
export function RestaurantsIllustration() {
  const id = "irs";
  return (
    <Base id={id} label="Restaurant point of sale — tableside ordering tablet with itemized check">
      {/* HERO — tablet, tilted */}
      <g transform="translate(120 40) rotate(-4 130 70)" filter={`url(#${id}-shadow)`}>
        <rect width="260" height="150" rx="14" fill={`url(#${id}-card)`} stroke="#061A3A" strokeOpacity="0.08" />
        <rect width="260" height="26" rx="14" fill={`url(#${id}-navy)`} />
        <rect y="14" width="260" height="12" fill="#061A3A" />
        <circle cx="16" cy="13" r="3.4" fill="#0072FF" />
        <rect x="30" y="8" width="90" height="10" rx="3" fill="#FFFFFF" fillOpacity="0.15" />
        <text x="34" y="17" fontFamily="Inter, system-ui, sans-serif" fontSize="8.5" fontWeight="700" fill="#FFFFFF" fillOpacity="0.75">Table 12</text>
        <rect x="220" y="8" width="30" height="10" rx="5" fill={`url(#${id}-accent)`} />

        {/* menu list */}
        <g transform="translate(18 40)" fontFamily="Inter, system-ui, sans-serif">
          {[
            { t: "Ribeye Steak", p: "$32", sel: true },
            { t: "Caesar Salad", p: "$14" },
            { t: "House Wine", p: "$11" },
            { t: "Tiramisu", p: "$9" },
          ].map((it, i) => (
            <g key={i} transform={`translate(0 ${i * 22})`}>
              <rect width="224" height="18" rx="5" fill={it.sel ? "#EAF3FF" : "#FFFFFF"} />
              {it.sel && <rect x="0" y="0" width="3" height="18" rx="1.5" fill="#0072FF" />}
              <text x="12" y="12" fontSize="9.5" fontWeight={it.sel ? 800 : 700} fill="#061A3A">{it.t}</text>
              <text x="212" y="12" textAnchor="end" fontSize="9.5" fontWeight="800" fill={it.sel ? "#0072FF" : "#061A3A"}>{it.p}</text>
            </g>
          ))}
        </g>
      </g>

      {/* FLOATING RECEIPT CARD — top-right */}
      <g transform="translate(468 30)" filter={`url(#${id}-shadow)`}>
        <rect width="180" height="112" rx="14" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <rect x="0" y="0" width="180" height="5" rx="2.5" fill={`url(#${id}-accent)`} />
        <text x="14" y="22" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="700" fill="#061A3A" opacity="0.55">Check total</text>
        <text x="14" y="46" fontFamily="Inter, system-ui, sans-serif" fontSize="22" fontWeight="800" fill="#061A3A">$66.00</text>
        <g transform="translate(14 58)">
          <rect width="60" height="4" rx="2" fill="#061A3A" fillOpacity="0.25" />
          <rect y="10" width="40" height="4" rx="2" fill="#061A3A" fillOpacity="0.2" />
        </g>
        <g transform="translate(14 82)">
          <rect width="72" height="20" rx="10" fill={`url(#${id}-accent)`} />
          <text x="10" y="14" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="800" fill="#FFFFFF">Split · 4</text>
          <rect x="80" y="0" width="72" height="20" rx="10" fill="#EAF3FF" />
          <text x="90" y="14" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="800" fill="#0072FF">Pay now</text>
        </g>
      </g>

      {/* FLOATING TIP PILL — bottom-right */}
      <g transform="translate(486 156)" filter={`url(#${id}-soft)`}>
        <rect width="156" height="38" rx="19" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <circle cx="19" cy="19" r="11" fill={`url(#${id}-accent)`} />
        <path d="M14 20 L18 24 L26 15" stroke="#FFFFFF" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        <text x="38" y="16" fontFamily="Inter, system-ui, sans-serif" fontSize="8.5" fontWeight="700" fill="#061A3A" opacity="0.55">Tip added</text>
        <text x="38" y="28" fontFamily="Inter, system-ui, sans-serif" fontSize="11" fontWeight="800" fill="#061A3A">+ 20% · $13.20</text>
      </g>

      {/* pulse */}
      <circle cx="94" cy="182" r="3" fill="#0072FF">
        <animate attributeName="fill-opacity" values="0.2;1;0.2" dur="2.6s" repeatCount="indefinite" />
      </circle>
    </Base>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   2) DELIS & CONVENIENCE STORES
   Hero: countertop terminal with quick-tap items.
   Orbit: barcode scanner card + age check pill.
   ══════════════════════════════════════════════════════════════════════ */
export function DelisIllustration() {
  const id = "ide";
  return (
    <Base id={id} label="Deli and convenience POS — quick-tap items with barcode scan">
      {/* HERO — POS terminal with quick grid */}
      <g transform="translate(122 30)" filter={`url(#${id}-shadow)`}>
        <rect width="240" height="164" rx="14" fill={`url(#${id}-card)`} stroke="#061A3A" strokeOpacity="0.08" />
        <rect width="240" height="28" rx="14" fill={`url(#${id}-navy)`} />
        <rect y="14" width="240" height="14" fill="#061A3A" />
        <text x="14" y="18" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="800" fill="#FFFFFF">Quick sale</text>
        <rect x="196" y="9" width="32" height="10" rx="5" fill={`url(#${id}-accent)`} />
        {/* subtotal band */}
        <g transform="translate(14 40)">
          <rect width="212" height="30" rx="8" fill="#EAF3FF" />
          <text x="10" y="14" fontFamily="Inter, system-ui, sans-serif" fontSize="8" fontWeight="700" fill="#061A3A" opacity="0.55">Subtotal</text>
          <text x="10" y="26" fontFamily="Inter, system-ui, sans-serif" fontSize="13" fontWeight="800" fill="#061A3A">$18.47</text>
          <text x="204" y="22" textAnchor="end" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="800" fill="#0072FF">3 items</text>
        </g>
        {/* 3x2 quick tiles */}
        <g transform="translate(14 80)">
          {[0,1,2,3,4,5].map((i) => {
            const x = (i % 3) * 72;
            const y = Math.floor(i / 3) * 38;
            const primary = i === 1;
            return (
              <g key={i} transform={`translate(${x} ${y})`}>
                <rect width="64" height="30" rx="7" fill={primary ? "#0072FF" : "#FFFFFF"} stroke="#061A3A" strokeOpacity={primary ? 0 : 0.08} />
                <rect x="8" y="8" width="14" height="14" rx="3" fill={primary ? "#FFFFFF" : "#EAF3FF"} />
                <rect x="26" y="10" width="30" height="4" rx="2" fill={primary ? "#FFFFFF" : "#061A3A"} fillOpacity={primary ? 0.9 : 0.8} />
                <rect x="26" y="18" width="20" height="3" rx="1.5" fill={primary ? "#FFFFFF" : "#061A3A"} fillOpacity={primary ? 0.7 : 0.4} />
              </g>
            );
          })}
        </g>
      </g>

      {/* FLOATING BARCODE SCAN CARD — top-right */}
      <g transform="translate(466 30)" filter={`url(#${id}-shadow)`}>
        <rect width="184" height="90" rx="14" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <rect x="0" y="0" width="184" height="5" rx="2.5" fill={`url(#${id}-accent)`} />
        <text x="14" y="22" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="700" fill="#061A3A" opacity="0.55">Scan &amp; ring</text>
        {/* barcode */}
        <g transform="translate(14 32)">
          {[0,3,4,7,10,11,14,17,20,21,24,27,30,33,36,39,42,45,48,51,54,57,60,63,66,69,72,75,78].map((x, i) => (
            <rect key={x} x={x} y="0" width={i % 4 === 0 ? 3 : 1.6} height="30" fill="#061A3A" />
          ))}
        </g>
        <text x="14" y="76" fontFamily="ui-monospace, Menlo, monospace" fontSize="9" fontWeight="700" fill="#061A3A" opacity="0.6">742511 · $2.99</text>
      </g>

      {/* FLOATING AGE-CHECK PILL — bottom-right */}
      <g transform="translate(484 138)" filter={`url(#${id}-soft)`}>
        <rect width="150" height="52" rx="14" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <g transform="translate(12 12)">
          <circle cx="14" cy="14" r="14" fill={`url(#${id}-accent)`} />
          <text x="14" y="18" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontWeight="900" fontSize="9" fill="#FFFFFF">21+</text>
        </g>
        <text x="44" y="22" fontFamily="Inter, system-ui, sans-serif" fontSize="8.5" fontWeight="700" fill="#061A3A" opacity="0.55">ID prompt</text>
        <text x="44" y="36" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="800" fill="#061A3A">Verified · 08/12/24</text>
      </g>

      <circle cx="600" cy="120" r="3" fill="#0072FF">
        <animate attributeName="fill-opacity" values="0.2;1;0.2" dur="2.8s" repeatCount="indefinite" />
      </circle>
    </Base>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   3) GROCERY STORES
   Hero: lane checkout screen with scanning list.
   Orbit: EBT-approved card + weight-scale chip.
   ══════════════════════════════════════════════════════════════════════ */
export function GroceryIllustration() {
  const id = "igr";
  return (
    <Base id={id} label="Grocery lane checkout — running total and EBT-ready payment">
      {/* HERO — lane checkout display */}
      <g transform="translate(112 30)" filter={`url(#${id}-shadow)`}>
        <rect width="256" height="160" rx="14" fill={`url(#${id}-card)`} stroke="#061A3A" strokeOpacity="0.08" />
        {/* topbar */}
        <rect width="256" height="26" rx="14" fill={`url(#${id}-navy)`} />
        <rect y="14" width="256" height="12" fill="#061A3A" />
        <text x="14" y="18" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="800" fill="#FFFFFF">Lane 03 · Ringing</text>
        <circle cx="234" cy="13" r="4" fill="#3A8EFF" />
        {/* items scanned list */}
        <g transform="translate(14 38)" fontFamily="Inter, system-ui, sans-serif">
          {[
            { t: "Bananas · 2.1 lb", p: "$1.24" },
            { t: "Whole milk 1gal", p: "$4.29" },
            { t: "Eggs · dozen", p: "$3.79" },
            { t: "Sourdough loaf", p: "$5.49" },
          ].map((it, i) => (
            <g key={i} transform={`translate(0 ${i * 18})`}>
              <circle cx="4" cy="8" r="3" fill="#0072FF" />
              <text x="14" y="12" fontSize="9" fontWeight="700" fill="#061A3A">{it.t}</text>
              <text x="220" y="12" textAnchor="end" fontSize="9" fontWeight="800" fill="#061A3A">{it.p}</text>
            </g>
          ))}
        </g>
        {/* total band */}
        <g transform="translate(14 118)">
          <rect width="228" height="30" rx="8" fill="#EAF3FF" />
          <text x="10" y="14" fontFamily="Inter, system-ui, sans-serif" fontSize="8" fontWeight="700" fill="#061A3A" opacity="0.55">Running total</text>
          <text x="10" y="26" fontFamily="Inter, system-ui, sans-serif" fontSize="13" fontWeight="800" fill="#061A3A">$14.81</text>
          <rect x="150" y="6" width="70" height="18" rx="9" fill={`url(#${id}-accent)`} />
          <text x="184" y="18" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="800" fill="#FFFFFF">Pay now</text>
        </g>
      </g>

      {/* FLOATING EBT-READY PAYMENT CARD — top-right */}
      <g transform="translate(456 32)" filter={`url(#${id}-shadow)`}>
        <rect width="196" height="86" rx="14" fill={`url(#${id}-navy)`} />
        <rect width="196" height="30" rx="14" fill={`url(#${id}-accent)`} />
        <rect y="16" width="196" height="14" fill="#0072FF" />
        <text x="14" y="20" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="800" fill="#FFFFFF">EBT · Accepted</text>
        {/* chip */}
        <rect x="14" y="42" width="26" height="20" rx="4" fill="#3A8EFF" />
        <line x1="14" y1="47" x2="40" y2="47" stroke="#061A3A" strokeOpacity="0.6" />
        <line x1="14" y1="54" x2="40" y2="54" stroke="#061A3A" strokeOpacity="0.6" />
        {/* number groups */}
        <rect x="52"  y="52" width="24" height="4" rx="2" fill="#FFFFFF" fillOpacity="0.9" />
        <rect x="84"  y="52" width="24" height="4" rx="2" fill="#FFFFFF" fillOpacity="0.55" />
        <rect x="116" y="52" width="24" height="4" rx="2" fill="#FFFFFF" fillOpacity="0.55" />
        <rect x="148" y="52" width="30" height="4" rx="2" fill="#FFFFFF" />
        {/* contactless */}
        <g transform="translate(160 30)" stroke="#FFFFFF" strokeWidth="1.6" fill="none" strokeLinecap="round">
          <path d="M0 8 Q4 4 8 8" />
          <path d="M-3 12 Q4 0 12 12" />
        </g>
        <text x="14" y="76" fontFamily="Inter, system-ui, sans-serif" fontSize="8.5" fontWeight="700" fill="#FFFFFF" fillOpacity="0.65">Multi-tender · SNAP · Credit</text>
      </g>

      {/* FLOATING WEIGHT/SCALE CHIP — bottom-right */}
      <g transform="translate(486 138)" filter={`url(#${id}-soft)`}>
        <rect width="148" height="52" rx="14" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <g transform="translate(12 12)">
          <circle cx="14" cy="14" r="14" fill="#EAF3FF" />
          <path d="M14 6 L20 22 L8 22 Z" fill="#0072FF" />
          <rect x="6" y="22" width="16" height="3" rx="1.5" fill="#0072FF" />
        </g>
        <text x="44" y="22" fontFamily="Inter, system-ui, sans-serif" fontSize="8.5" fontWeight="700" fill="#061A3A" opacity="0.55">Produce scale</text>
        <text x="44" y="36" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="800" fill="#061A3A">2.10 lb · $0.59/lb</text>
      </g>

      <circle cx="86" cy="184" r="3" fill="#0072FF">
        <animate attributeName="fill-opacity" values="0.2;1;0.2" dur="3s" repeatCount="indefinite" />
      </circle>
    </Base>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   4) SMOKE SHOPS
   Hero: ID-verification prompt on tablet + 21+ badge.
   Orbit: cash-discount receipt card.
   ══════════════════════════════════════════════════════════════════════ */
export function SmokeShopsIllustration() {
  const id = "ism";
  return (
    <Base id={id} label="Smoke shop POS with age verification prompt and cash discount pricing">
      {/* HERO — verification tablet */}
      <g transform="translate(128 30)" filter={`url(#${id}-shadow)`}>
        <rect width="240" height="160" rx="14" fill={`url(#${id}-card)`} stroke="#061A3A" strokeOpacity="0.08" />
        <rect width="240" height="28" rx="14" fill={`url(#${id}-navy)`} />
        <rect y="14" width="240" height="14" fill="#061A3A" />
        <text x="14" y="18" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="800" fill="#FFFFFF">Age verification</text>
        <circle cx="220" cy="14" r="4" fill="#3A8EFF" />
        {/* 21+ shield */}
        <g transform="translate(46 40)">
          <path d="M74 0 L148 22 V70 C148 106 116 132 74 148 C32 132 0 106 0 70 V22 Z" fill={`url(#${id}-navy)`} />
          <path d="M74 12 L136 32 V70 C136 100 108 122 74 134 C40 122 12 100 12 70 V32 Z" fill={`url(#${id}-accent)`} fillOpacity="0.28" />
          <text x="74" y="86" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="30" fontWeight="900" fill="#FFFFFF">21+</text>
          <text x="74" y="112" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="800" fill="#FFFFFF" fillOpacity="0.85">SCAN ID</text>
        </g>
      </g>

      {/* FLOATING CASH-DISCOUNT RECEIPT — top-right */}
      <g transform="translate(460 32)" filter={`url(#${id}-shadow)`}>
        <rect width="192" height="112" rx="14" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <rect x="0" y="0" width="192" height="5" rx="2.5" fill={`url(#${id}-accent)`} />
        <text x="14" y="22" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="700" fill="#061A3A" opacity="0.55">Dual pricing</text>
        <g transform="translate(14 30)">
          <rect width="164" height="26" rx="6" fill="#EAF3FF" />
          <text x="10" y="17" fontFamily="Inter, system-ui, sans-serif" fontSize="9.5" fontWeight="700" fill="#061A3A">Card price</text>
          <text x="154" y="17" textAnchor="end" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="800" fill="#061A3A">$10.30</text>
        </g>
        <g transform="translate(14 60)">
          <rect width="164" height="26" rx="6" fill={`url(#${id}-accent)`} />
          <text x="10" y="17" fontFamily="Inter, system-ui, sans-serif" fontSize="9.5" fontWeight="800" fill="#FFFFFF">Cash price</text>
          <text x="154" y="17" textAnchor="end" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="900" fill="#FFFFFF">$10.00</text>
        </g>
        <text x="14" y="102" fontFamily="Inter, system-ui, sans-serif" fontSize="8.5" fontWeight="700" fill="#061A3A" opacity="0.55">You save 3% every card sale</text>
      </g>

      {/* small pill — scan verified */}
      <g transform="translate(484 158)" filter={`url(#${id}-soft)`}>
        <rect width="148" height="34" rx="17" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <circle cx="18" cy="17" r="9" fill={`url(#${id}-accent)`} />
        <path d="M13 17 L16 20 L23 13" stroke="#FFFFFF" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        <text x="34" y="22" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="800" fill="#061A3A">ID verified</text>
      </g>

      <circle cx="616" cy="118" r="3" fill="#0072FF">
        <animate attributeName="fill-opacity" values="0.2;1;0.2" dur="2.6s" repeatCount="indefinite" />
      </circle>
    </Base>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   5) RETAIL STORES
   Hero: dashboard/inventory panel.
   Orbit: price-tag card + gift card.
   ══════════════════════════════════════════════════════════════════════ */
export function RetailIllustration() {
  const id = "irt";
  return (
    <Base id={id} label="Retail POS — inventory dashboard with price tag and gift card">
      {/* HERO — inventory dashboard */}
      <g transform="translate(72 30)" filter={`url(#${id}-shadow)`}>
        <rect width="288" height="160" rx="14" fill={`url(#${id}-card)`} stroke="#061A3A" strokeOpacity="0.08" />
        <rect width="288" height="26" rx="14" fill={`url(#${id}-navy)`} />
        <rect y="14" width="288" height="12" fill="#061A3A" />
        <text x="14" y="18" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="800" fill="#FFFFFF">Inventory · SKU 4,218</text>
        <rect x="240" y="8" width="36" height="10" rx="5" fill={`url(#${id}-accent)`} />
        {/* KPI row */}
        <g transform="translate(14 36)">
          {[
            { l: "In stock", v: "3,942", c: "#0072FF" },
            { l: "Low", v: "184", c: "#3A8EFF" },
            { l: "Sold today", v: "92", c: "#061A3A" },
          ].map((k, i) => (
            <g key={i} transform={`translate(${i * 90} 0)`}>
              <rect width="82" height="42" rx="8" fill="#EAF3FF" />
              <text x="10" y="16" fontFamily="Inter, system-ui, sans-serif" fontSize="8" fontWeight="700" fill="#061A3A" opacity="0.55">{k.l}</text>
              <text x="10" y="32" fontFamily="Inter, system-ui, sans-serif" fontSize="13" fontWeight="800" fill={k.c}>{k.v}</text>
            </g>
          ))}
        </g>
        {/* mini bar chart */}
        <g transform="translate(14 92)">
          {[36, 22, 44, 30, 52, 28, 46, 34, 58, 38, 50, 42].map((h, i) => (
            <rect key={i} x={i * 22} y={56 - h} width="14" height={h} rx="2" fill={i === 8 ? "#0072FF" : "#3A8EFF"} fillOpacity={i === 8 ? 1 : 0.55} />
          ))}
        </g>
      </g>

      {/* FLOATING PRICE TAG — top-right */}
      <g transform="translate(448 32)" filter={`url(#${id}-shadow)`}>
        <path d="M14 0 L92 0 L110 20 L92 96 L14 96 L-4 76 Z M-4 76 L14 0" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <circle cx="14" cy="20" r="6" fill={`url(#${id}-accent)`} />
        <circle cx="14" cy="20" r="2.4" fill="#FFFFFF" />
        <text x="52" y="46" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="15" fontWeight="900" fill="#061A3A">$42</text>
        <text x="52" y="60" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="8" fontWeight="700" fill="#0072FF">MEMBER PRICE</text>
        {/* barcode */}
        <g transform="translate(20 68)">
          {[0,3,5,8,11,14,17,20,23,26,29,32,35,38,41,44,47,50,53,56,59,62].map((x, i) => (
            <rect key={x} x={x} y="0" width={i % 3 === 0 ? 2.4 : 1.2} height="18" fill="#061A3A" />
          ))}
        </g>
      </g>

      {/* FLOATING GIFT CARD — bottom-right */}
      <g transform="translate(566 42) rotate(6 66 44)" filter={`url(#${id}-shadow)`}>
        <rect width="132" height="86" rx="12" fill={`url(#${id}-navy)`} />
        <rect width="132" height="28" rx="12" fill={`url(#${id}-accent)`} />
        <rect y="16" width="132" height="12" fill="#0072FF" />
        <text x="12" y="18" fontFamily="Inter, system-ui, sans-serif" fontSize="8" fontWeight="800" fill="#FFFFFF">GIFT CARD</text>
        <text x="12" y="52" fontFamily="Inter, system-ui, sans-serif" fontSize="16" fontWeight="900" fill="#FFFFFF">$50.00</text>
        <text x="12" y="74" fontFamily="Inter, system-ui, sans-serif" fontSize="7.5" fontWeight="700" fill="#FFFFFF" fillOpacity="0.7">Loyalty · Reload anytime</text>
      </g>

      {/* FLOATING LOYALTY PILL — bottom-right */}
      <g transform="translate(474 158)" filter={`url(#${id}-soft)`}>
        <rect width="146" height="36" rx="18" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <circle cx="18" cy="18" r="10" fill="#EAF3FF" />
        <path d="M18 12 L20 16 L24 16 L21 19 L22 23 L18 21 L14 23 L15 19 L12 16 L16 16 Z" fill="#0072FF" />
        <text x="34" y="22" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="800" fill="#061A3A">Loyalty +30 pts</text>
      </g>

      <circle cx="90" cy="184" r="3" fill="#0072FF">
        <animate attributeName="fill-opacity" values="0.2;1;0.2" dur="2.8s" repeatCount="indefinite" />
      </circle>
    </Base>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   6) SALONS & SPAS
   Hero: appointment calendar panel.
   Orbit: services chip + tip pill.
   ══════════════════════════════════════════════════════════════════════ */
export function SalonsIllustration() {
  const id = "isa";
  return (
    <Base id={id} label="Salon booking panel with services and tip">
      {/* HERO — appointment calendar */}
      <g transform="translate(96 28)" filter={`url(#${id}-shadow)`}>
        <rect width="256" height="164" rx="14" fill={`url(#${id}-card)`} stroke="#061A3A" strokeOpacity="0.08" />
        <rect width="256" height="30" rx="14" fill={`url(#${id}-navy)`} />
        <rect y="16" width="256" height="14" fill="#061A3A" />
        <text x="14" y="20" fontFamily="Inter, system-ui, sans-serif" fontSize="9.5" fontWeight="800" fill="#FFFFFF">Bookings · Thursday</text>
        <circle cx="234" cy="15" r="4" fill="#3A8EFF" />

        {/* time slots */}
        <g transform="translate(14 42)">
          {[
            { t: "10:00 AM", s: "Cut & style", n: "Emma R.", hi: false },
            { t: "11:30 AM", s: "Color · full", n: "Priya K.",  hi: true  },
            { t: "1:00 PM",  s: "Blowout",     n: "Marcus L.", hi: false },
            { t: "2:45 PM",  s: "Beard trim",  n: "Jayden W.", hi: false },
          ].map((it, i) => (
            <g key={i} transform={`translate(0 ${i * 26})`}>
              <rect width="228" height="22" rx="6" fill={it.hi ? "#EAF3FF" : "#FFFFFF"} stroke="#061A3A" strokeOpacity={it.hi ? 0 : 0.06} />
              {it.hi && <rect x="0" y="0" width="3" height="22" rx="1.5" fill="#0072FF" />}
              <text x="10" y="14" fontFamily="Inter, system-ui, sans-serif" fontSize="8.5" fontWeight="800" fill={it.hi ? "#0072FF" : "#061A3A"}>{it.t}</text>
              <text x="76" y="14" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="700" fill="#061A3A">{it.s}</text>
              <text x="220" y="14" textAnchor="end" fontFamily="Inter, system-ui, sans-serif" fontSize="8.5" fontWeight="700" fill="#061A3A" opacity="0.55">{it.n}</text>
            </g>
          ))}
        </g>
      </g>

      {/* FLOATING SERVICES CARD — top-right (scissors icon) */}
      <g transform="translate(454 32)" filter={`url(#${id}-shadow)`}>
        <rect width="196" height="88" rx="14" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <rect x="0" y="0" width="196" height="5" rx="2.5" fill={`url(#${id}-accent)`} />
        <g transform="translate(14 20)">
          <g fill="none" stroke="#0072FF" strokeWidth="2.4" strokeLinecap="round">
            <circle cx="10" cy="12" r="8" />
            <circle cx="10" cy="34" r="8" />
            <path d="M18 16 L48 23 L34 23 L18 30" />
            <path d="M18 16 L48 23 M18 30 L48 23" />
          </g>
        </g>
        <text x="68" y="26" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="700" fill="#061A3A" opacity="0.55">Service booked</text>
        <text x="68" y="42" fontFamily="Inter, system-ui, sans-serif" fontSize="12" fontWeight="800" fill="#061A3A">Color · $145</text>
        <g transform="translate(68 52)">
          <rect width="60" height="18" rx="9" fill={`url(#${id}-accent)`} />
          <text x="30" y="12" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="8.5" fontWeight="800" fill="#FFFFFF">Confirm</text>
          <rect x="68" y="0" width="60" height="18" rx="9" fill="#EAF3FF" />
          <text x="98" y="12" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="8.5" fontWeight="800" fill="#0072FF">Reschedule</text>
        </g>
      </g>

      {/* FLOATING TIP PILL — bottom-right */}
      <g transform="translate(484 148)" filter={`url(#${id}-soft)`}>
        <rect width="150" height="42" rx="21" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <circle cx="21" cy="21" r="12" fill={`url(#${id}-accent)`} />
        <text x="21" y="25" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="12" fontWeight="900" fill="#FFFFFF">$</text>
        <text x="40" y="18" fontFamily="Inter, system-ui, sans-serif" fontSize="8.5" fontWeight="700" fill="#061A3A" opacity="0.55">Stylist tip</text>
        <text x="40" y="30" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="800" fill="#061A3A">+ 22% · $32</text>
      </g>

      <circle cx="80" cy="184" r="3" fill="#0072FF">
        <animate attributeName="fill-opacity" values="0.2;1;0.2" dur="3s" repeatCount="indefinite" />
      </circle>
    </Base>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   7) SERVICE BUSINESSES
   Hero: mobile-in-the-field invoice.
   Orbit: PAID badge + recurring billing chip.
   ══════════════════════════════════════════════════════════════════════ */
export function ServiceIllustration() {
  const id = "isv";
  return (
    <Base id={id} label="Field service invoice on mobile with PAID stamp and recurring billing">
      {/* HERO — phone invoice */}
      <g transform="translate(140 18) rotate(-3 90 92)" filter={`url(#${id}-shadow)`}>
        <rect width="180" height="184" rx="20" fill={`url(#${id}-card)`} stroke="#061A3A" strokeOpacity="0.08" />
        <rect x="10" y="10" width="160" height="164" rx="12" fill="#FFFFFF" />
        <rect x="10" y="10" width="160" height="26" rx="12" fill={`url(#${id}-navy)`} />
        <rect x="10" y="22" width="160" height="14" fill="#061A3A" />
        <text x="20" y="26" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="800" fill="#FFFFFF">Invoice · #2081</text>
        <circle cx="158" cy="23" r="3.5" fill="#3A8EFF" />
        {/* invoice body */}
        <g transform="translate(20 46)">
          <text fontFamily="Inter, system-ui, sans-serif" fontSize="8" fontWeight="700" fill="#061A3A" opacity="0.55">Client</text>
          <text y="12" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="800" fill="#061A3A">Northside HVAC</text>
          {[
            { t: "Diagnostic",    p: "$85" },
            { t: "Compressor",    p: "$420" },
            { t: "Labor · 2 hr",  p: "$180" },
          ].map((it, i) => (
            <g key={i} transform={`translate(0 ${28 + i * 16})`}>
              <text fontFamily="Inter, system-ui, sans-serif" fontSize="8.5" fontWeight="700" fill="#061A3A">{it.t}</text>
              <text x="140" textAnchor="end" fontFamily="Inter, system-ui, sans-serif" fontSize="8.5" fontWeight="800" fill="#061A3A">{it.p}</text>
            </g>
          ))}
          <line x1="0" y1="86" x2="140" y2="86" stroke="#061A3A" strokeOpacity="0.15" />
          <text y="102" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="700" fill="#061A3A" opacity="0.55">Total</text>
          <text x="140" y="102" textAnchor="end" fontFamily="Inter, system-ui, sans-serif" fontSize="13" fontWeight="800" fill="#061A3A">$685</text>
          <rect x="0" y="110" width="140" height="18" rx="9" fill={`url(#${id}-accent)`} />
          <text x="70" y="122" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="800" fill="#FFFFFF">Tap to pay</text>
        </g>
      </g>

      {/* PAID stamp */}
      <g transform="translate(80 92) rotate(-14 44 22)" filter={`url(#${id}-soft)`}>
        <rect width="88" height="44" rx="6" fill="none" stroke="#0072FF" strokeWidth="3" />
        <text x="44" y="30" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="18" fontWeight="900" fill="#0072FF">PAID</text>
      </g>

      {/* FLOATING RECURRING BILLING CARD — right */}
      <g transform="translate(440 34)" filter={`url(#${id}-shadow)`}>
        <rect width="212" height="90" rx="14" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <rect x="0" y="0" width="212" height="5" rx="2.5" fill={`url(#${id}-accent)`} />
        <text x="14" y="22" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="700" fill="#061A3A" opacity="0.55">Recurring plan</text>
        <text x="14" y="42" fontFamily="Inter, system-ui, sans-serif" fontSize="13" fontWeight="800" fill="#061A3A">Maintenance · Monthly</text>
        {/* 6 dot months */}
        <g transform="translate(14 56)">
          {[0,1,2,3,4,5].map((i) => (
            <g key={i} transform={`translate(${i * 30} 0)`}>
              <circle cx="8" cy="8" r="8" fill={i < 4 ? "#0072FF" : "#EAF3FF"} />
              {i < 4 && <path d="M5 8 L7 10 L11 6" stroke="#FFFFFF" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />}
              <text x="8" y="28" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="7.5" fontWeight="700" fill="#061A3A" opacity="0.55">
                {["Jan","Feb","Mar","Apr","May","Jun"][i]}
              </text>
            </g>
          ))}
        </g>
      </g>

      {/* FLOATING FIELD PILL — bottom-right */}
      <g transform="translate(478 152)" filter={`url(#${id}-soft)`}>
        <rect width="156" height="36" rx="18" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <circle cx="18" cy="18" r="10" fill={`url(#${id}-accent)`} />
        <path d="M18 12 L18 24 M12 18 L24 18" stroke="#FFFFFF" strokeWidth="2.4" strokeLinecap="round" />
        <text x="34" y="14" fontFamily="Inter, system-ui, sans-serif" fontSize="7.5" fontWeight="700" fill="#061A3A" opacity="0.55">On-site payment</text>
        <text x="34" y="27" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="800" fill="#061A3A">Tap · Chip · Text-to-pay</text>
      </g>

      <circle cx="80" cy="176" r="3" fill="#0072FF">
        <animate attributeName="fill-opacity" values="0.2;1;0.2" dur="2.6s" repeatCount="indefinite" />
      </circle>
    </Base>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   8) E-COMMERCE
   Hero: browser checkout with product + total.
   Orbit: shopping-bag card + Apple/Google Pay pill.
   ══════════════════════════════════════════════════════════════════════ */
export function EcommerceIllustration() {
  const id = "iec";
  return (
    <Base id={id} label="E-commerce checkout in browser with hosted payment and mobile wallets">
      {/* HERO — browser checkout */}
      <g transform="translate(94 26)" filter={`url(#${id}-shadow)`}>
        <rect width="300" height="168" rx="14" fill={`url(#${id}-card)`} stroke="#061A3A" strokeOpacity="0.08" />
        {/* browser bar */}
        <rect width="300" height="26" rx="14" fill={`url(#${id}-navy)`} />
        <rect y="14" width="300" height="12" fill="#061A3A" />
        <circle cx="14" cy="13" r="3.4" fill="#0072FF" />
        <circle cx="26" cy="13" r="3.4" fill="#3A8EFF" />
        <circle cx="38" cy="13" r="3.4" fill="#FFFFFF" fillOpacity="0.5" />
        <rect x="60" y="7" width="192" height="12" rx="6" fill="#FFFFFF" fillOpacity="0.12" />
        <rect x="66" y="10" width="10" height="6" rx="2" fill="#3A8EFF" />
        <text x="82" y="16" fontFamily="ui-monospace, Menlo, monospace" fontSize="7.5" fontWeight="700" fill="#FFFFFF" fillOpacity="0.7">shop.example.com/checkout</text>
        {/* left product */}
        <g transform="translate(16 38)">
          <rect width="112" height="116" rx="10" fill="#EAF3FF" />
          <rect x="20" y="22" width="72" height="52" rx="8" fill={`url(#${id}-accent)`} fillOpacity="0.28" />
          <path d="M32 62 L48 44 L60 56 L80 36 L92 50" fill="none" stroke="#0072FF" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx="52" cy="46" r="4" fill="#0072FF" />
          <rect x="18" y="90" width="70" height="6" rx="3" fill="#061A3A" />
          <rect x="18" y="102" width="50" height="4" rx="2" fill="#061A3A" fillOpacity="0.55" />
        </g>
        {/* right form */}
        <g transform="translate(140 38)">
          <text fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="700" fill="#061A3A" opacity="0.55">Payment</text>
          <rect y="8" width="146" height="22" rx="6" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.1" />
          <text x="8" y="22" fontFamily="ui-monospace, Menlo, monospace" fontSize="9" fontWeight="700" fill="#061A3A" opacity="0.75">4242 4242 ••• 4242</text>
          <rect y="36" width="70" height="22" rx="6" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.1" />
          <text x="8" y="50" fontFamily="ui-monospace, Menlo, monospace" fontSize="9" fontWeight="700" fill="#061A3A" opacity="0.55">12/28</text>
          <rect x="76" y="36" width="70" height="22" rx="6" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.1" />
          <text x="84" y="50" fontFamily="ui-monospace, Menlo, monospace" fontSize="9" fontWeight="700" fill="#061A3A" opacity="0.55">•••</text>
          {/* total */}
          <text y="80" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="700" fill="#061A3A" opacity="0.55">Total</text>
          <text x="146" y="80" textAnchor="end" fontFamily="Inter, system-ui, sans-serif" fontSize="14" fontWeight="900" fill="#061A3A">$84.20</text>
          <rect y="90" width="146" height="22" rx="11" fill={`url(#${id}-accent)`} />
          <text x="73" y="105" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="9.5" fontWeight="800" fill="#FFFFFF">Place order · Secure</text>
        </g>
      </g>

      {/* FLOATING SHOPPING BAG — top-right */}
      <g transform="translate(480 30)" filter={`url(#${id}-shadow)`}>
        <path d="M0 22 L14 6 L58 6 L72 22 L72 96 L0 96 Z" fill={`url(#${id}-navy)`} />
        <path d="M20 6 V-2 A16 16 0 0 1 52 -2 V6" fill="none" stroke={`url(#${id}-navy)`} strokeWidth="4" transform="translate(0 6)" />
        <circle cx="24" cy="46" r="4" fill="#0072FF" />
        <circle cx="48" cy="46" r="4" fill="#0072FF" />
        <path d="M18 66 Q36 84 54 66" fill="none" stroke="#FFFFFF" strokeWidth="2.6" strokeLinecap="round" />
        {/* count badge */}
        <circle cx="66" cy="14" r="12" fill={`url(#${id}-accent)`} />
        <text x="66" y="18" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="11" fontWeight="900" fill="#FFFFFF">3</text>
      </g>

      {/* FLOATING WALLET PILL — bottom-right */}
      <g transform="translate(478 150)" filter={`url(#${id}-soft)`}>
        <rect width="164" height="42" rx="21" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <g transform="translate(10 8)">
          <rect width="36" height="26" rx="7" fill={`url(#${id}-navy)`} />
          <text x="18" y="18" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="7.5" fontWeight="900" fill="#FFFFFF">Pay</text>
        </g>
        <g transform="translate(50 8)">
          <rect width="36" height="26" rx="7" fill={`url(#${id}-accent)`} />
          <text x="18" y="18" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="7.5" fontWeight="900" fill="#FFFFFF">G Pay</text>
        </g>
        <text x="94" y="20" fontFamily="Inter, system-ui, sans-serif" fontSize="8.5" fontWeight="700" fill="#061A3A" opacity="0.55">Wallets</text>
        <text x="94" y="32" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="800" fill="#061A3A">1-tap · saved</text>
      </g>

      <circle cx="70" cy="184" r="3" fill="#0072FF">
        <animate attributeName="fill-opacity" values="0.2;1;0.2" dur="3s" repeatCount="indefinite" />
      </circle>
    </Base>
  );
}

const IndustryIllustrations = {
  restaurants: RestaurantsIllustration,
  delis: DelisIllustration,
  grocery: GroceryIllustration,
  "smoke-shops": SmokeShopsIllustration,
  retail: RetailIllustration,
  salons: SalonsIllustration,
  service: ServiceIllustration,
  ecommerce: EcommerceIllustration,
};

export default IndustryIllustrations;
